import {Button, Table, Tag} from 'antd';
import {useState} from 'react';

import {PublicationPreviewThumb} from '../../../../component/posts/publication-preview-thumb/publication-preview-thumb';
import {getYandexStoryStatusConfig} from '../../../../component/stories/stories-helper';
import {YandexStorySelectedCompanies} from '../../../../component/stories/yandex-stories-preview/selected-companies/yandex-story-selected-companies';
import {YandexStoriesPreview} from '../../../../component/stories/yandex-stories-preview/yandex-stories-preview';
import {Locale} from '../../../../provider/locale/locale';
import {PublicationListFilterType} from '../../../../service/publication/publication-filter-type';
import {useYandexPreview, useYandexStories} from '../../../../service/yandex-stories/yandex-stories-hook';
import {YandexStoryListItemType} from '../../../../service/yandex-stories/yandex-stories-type';
import {useFormat} from '../../../../util/format-hook/format-hook';
import {noop} from '../../../../util/function';

import {storiesTableColumnList} from './stories-table-const';
import {StoriesTableRenderingType} from './stories-table-type';
import {StoryActions} from './story-actions';

type PropsType = {
    filter?: PublicationListFilterType;
};

export function StoriesTable({filter}: PropsType): JSX.Element {
    const {data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage} = useYandexStories(filter, {
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {getFormattedDateTime} = useFormat();
    const [editingStoryId, setEditingStoryId] = useState<number | null>(null);

    const storiesData = data?.pages.flatMap((page) => page.results) ?? [];

    const {activeStoryId, nextStoryId, previousStoryId, onChangeStoryId} = useYandexPreview(
        storiesData,
        hasNextPage ? fetchNextPage : noop
    );

    function handleEdit(storyId: number): void {
        setEditingStoryId(storyId);
        onChangeStoryId(storyId);
    }

    function handleChangeStoryId(storyId: number | null): void {
        onChangeStoryId(storyId);

        if (storyId !== editingStoryId) {
            setEditingStoryId(null);
        }
    }

    function renderStoryRow(story: YandexStoryListItemType): StoriesTableRenderingType {
        const statusConfig = getYandexStoryStatusConfig(story.status);

        return {
            key: String(story.id),
            title: story.name,
            preview: story.cover?.url ? (
                <PublicationPreviewThumb onClick={() => handleChangeStoryId(story.id)} src={story.cover.url} />
            ) : (
                '—'
            ),
            status: (
                <Tag color={statusConfig.tagColor}>
                    <Locale stringKey={statusConfig.langKey} />
                </Tag>
            ),
            publishAt: story.publishAt ? getFormattedDateTime(Date.parse(story.publishAt)) : '—',
            deleteAt: story.deleteAt ? getFormattedDateTime(Date.parse(story.deleteAt)) : '—',
            companiesCount: (
                <YandexStorySelectedCompanies companiesCount={story.companiesCount} key={story.id} storyId={story.id} />
            ),
            date: story.createdAt ? getFormattedDateTime(Date.parse(story.createdAt)) : '—',
            actions: <StoryActions onEdit={handleEdit} story={story} />,
        };
    }

    return (
        <>
            <Table<StoriesTableRenderingType>
                columns={storiesTableColumnList}
                dataSource={storiesData.map(renderStoryRow)}
                loading={isLoading}
                pagination={false}
                scroll={{x: true}}
                size="middle"
            />

            {hasNextPage && (
                <div style={{marginTop: 16, textAlign: 'center'}}>
                    <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                        <Locale stringKey="POSTS__STORIES_TABLE__LOAD_MORE" />
                    </Button>
                </div>
            )}

            <YandexStoriesPreview
                activeStoryId={activeStoryId}
                autoStartEdit={activeStoryId === editingStoryId && editingStoryId !== null}
                nextStoryId={nextStoryId}
                onChangeStoryId={handleChangeStoryId}
                previousStoryId={previousStoryId}
            />
        </>
    );
}
